import {
    defaultDataItemsEqualityComparer,
    ListViewDataSourceDataItem,
    ListViewItemId,
    ListViewRenderItemProps
} from './list-view-types';
import React from 'react';
import {ListViewContext, ListViewContextData} from './list-view-composable';
import {ListViewItemViewWrapper} from './list-view-item-view-wrapper';
import {EqualityComparer, iterableMapToArray} from '@wix/devzai-utils-common';
import {ListViewItemContext} from './list-view-item-context';
import {ListViewItemRenderFunction} from './list-view';

export interface ListViewItemsViewProps<T> {
    renderItem: ListViewItemRenderFunction<T>;
    dataItemEqualityComparer: EqualityComparer<T>;
    itemsWrapper?: (children: React.ReactElement[]) => React.ReactElement;
}

interface ComposableListViewItemsViewProps<T> extends ListViewItemsViewProps<T> {
    dataSource: ListViewDataSourceDataItem<T>[];
}

export const DefaultListViewItemsViewProps = {
    dataItemEqualityComparer: defaultDataItemsEqualityComparer
};

export class ListViewItemsView<T> extends React.Component<ComposableListViewItemsViewProps<T>> {
    public static defaultProps = DefaultListViewItemsViewProps;

    render() {
        return <ListViewContext.Consumer>{this.consumeListViewContextData}</ListViewContext.Consumer>;
    }

    private consumeListViewContextData = (listViewContextData: ListViewContextData | null) => {
        if (listViewContextData !== null) {
            const {
                dataSource,
                renderItem,
                itemsWrapper
            } = this.props;

            let nextItemIndex = 0;

            const children = iterableMapToArray(dataSource, (dataSourceItem: ListViewDataSourceDataItem<T>, skip) => {
                const itemIndex = nextItemIndex++;

                if (dataSourceItem.isExcluded) {
                    return skip;
                }

                const dataItemId = dataSourceItem.id;

                const isSelected = listViewContextData.selectedIdsSet.has(dataItemId);
                const isDisabled =
                    dataSourceItem.isDisabled === true || listViewContextData.disabledIdsSet.has(dataItemId);

                const renderItemProps: ListViewRenderItemProps<T> = {
                    itemIndex: itemIndex,
                    dataItemId: dataItemId,
                    dataItem: dataSourceItem.dataItem,
                    isSelected: isSelected,
                    isCurrent: listViewContextData.currentListViewItemId === dataItemId,
                    isDisabled: isDisabled,
                    isFocusingCursor: isFocusingCursor(dataItemId, listViewContextData, isDisabled),
                    listView: listViewContextData.listView,
                    tabIndex: resolveTabIndex(dataItemId, listViewContextData, isDisabled),
                    listViewId: listViewContextData.navigationListId
                };

                return (
                    <ListViewItemContext.Provider
                        key={dataItemId}
                        value={renderItemProps}
                    >
                        <ListViewItemViewWrapper
                            id={renderItemProps.dataItemId}
                            listViewContextData={listViewContextData}
                            renderProps={renderItemProps}
                        >
                            {renderItem(renderItemProps)}
                        </ListViewItemViewWrapper>
                    </ListViewItemContext.Provider>
                );
            });

            if (itemsWrapper) {
                return itemsWrapper(children);
            } else {
                return children;
            }
        } else {
            return null;
        }
    };
}

function isFocusingCursor(
    itemId: ListViewItemId,
    listViewContextData: ListViewContextData,
    isDisabled: boolean
): boolean {
    if (isDisabled) {
        return false;
    }

    const currentItemId = listViewContextData.currentListViewItemId;

    if (currentItemId === null) {
        return listViewContextData.firstNavigationScopeItemId === itemId;
    } else {
        return currentItemId === itemId;
    }
}

function resolveTabIndex(
    itemId: ListViewItemId,
    listViewContextData: ListViewContextData,
    isDisabled: boolean
): number | undefined {
    if (isDisabled) {
        return undefined;
    }

    const isFocusable = listViewContextData.isFocusable;

    if (!isFocusable) {
        return undefined;
    } else {
        return isFocusingCursor(itemId, listViewContextData, isDisabled) ? 0 : -1;
    }
}
